import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PERSONA_DEFINITIONS } from "../config/surveyConfig";
import { Sparkles, BookOpen, AlertTriangle, Home, Heart, PartyPopper } from "lucide-react";

export default function ThankYouPage() {
  const navigate = useNavigate();
  const result = useMemo(() => {
    const raw = sessionStorage.getItem("survey_result");
    return raw ? JSON.parse(raw) : null;
  }, []);

  if (!result) {
    return (
      <div className="min-h-screen bg-[#fdf7ff] flex items-center justify-center p-4">
        <div className="nb-card text-center max-w-md animate-slide-up" data-testid="no-result">
          <span className="text-4xl block mb-3">🤔</span>
          <h2 className="font-parkinsans text-2xl mb-3">Hmm, nothing here yet</h2>
          <p className="text-gray-500 mb-6">Looks like you haven't completed the survey. Want to give it a go?</p>
          <button onClick={() => navigate("/")} className="nb-btn-primary" data-testid="go-home-btn">
            <Home className="w-4 h-4" strokeWidth={2.5} /> Take the survey
          </button>
        </div>
      </div>
    );
  }

  const persona = result.persona || {};
  const personaDef = PERSONA_DEFINITIONS[persona.name] || {};

  return (
    <div className="min-h-screen bg-[#fdf7ff] relative overflow-hidden" data-testid="thank-you-page">
      {/* Celebration bg */}
      <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-[#f5d0fe]/30 animate-float-slow blur-2xl" />
      <div className="absolute top-20 right-10 w-56 h-56 rounded-full bg-[#a21caf]/10 animate-float-delayed blur-3xl" />
      <div className="absolute bottom-10 left-1/3 w-32 h-32 rounded-full bg-[#fef3c7]/50 animate-float-slow blur-xl" />

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-xl">
          {/* Celebration header */}
          <div className="nb-card mb-6 text-center animate-slide-up relative overflow-hidden" data-testid="thank-you-card">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#a21caf] via-[#f59e0b] to-[#22c55e]" />
            <div className="pt-4">
              <div className="text-6xl mb-4 animate-bounce-gentle">{persona.emoji || personaDef.emoji}</div>
              <h1 className="font-parkinsans text-3xl sm:text-4xl text-[#111] mb-2">You're amazing!</h1>
              <p className="text-gray-600 text-base leading-relaxed mb-5">
                That's it — you're all done! Here's a peek at your AI readiness profile. 
                Remember, this isn't a grade — it's just a snapshot of where you are right now.
              </p>

              {/* Persona badge */}
              <div className="inline-block mb-4">
                <div className="nb-badge text-lg px-6 py-2.5" data-testid="persona-badge"
                  style={{ backgroundColor: `${personaDef.color || '#a21caf'}20`, borderColor: personaDef.color || '#111' }}>
                  {persona.emoji} {persona.name}
                </div>
              </div>

              {personaDef.vibe && (
                <p className="text-base font-semibold text-[#a21caf] mb-3">{personaDef.vibe}</p>
              )}

              <p className="text-sm text-gray-500 leading-relaxed">
                {persona.description || personaDef.description}
              </p>
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <div className="nb-card-sm animate-slide-up" style={{ animationDelay: "100ms" }} data-testid="usage-card">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#a21caf]" strokeWidth={2.5} />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Your AI Usage</span>
              </div>
              <p className="font-semibold text-[#111] text-lg">{result.usage_level || "Not specified"}</p>
            </div>
            <div className="nb-card-sm animate-slide-up" style={{ animationDelay: "200ms" }} data-testid="learning-card">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-[#3b82f6]" strokeWidth={2.5} />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Want to Learn</span>
              </div>
              <div className="space-y-1">
                {(result.learning_goals || []).slice(0, 3).map((g, i) => (
                  <p key={i} className="text-sm text-[#111]">{g}</p>
                ))}
                {(!result.learning_goals || result.learning_goals.length === 0) && (
                  <p className="text-sm text-gray-400">Nothing selected yet</p>
                )}
              </div>
            </div>
            <div className="nb-card-sm animate-slide-up" style={{ animationDelay: "300ms" }} data-testid="concerns-card">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-[#f59e0b]" strokeWidth={2.5} />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">On Your Mind</span>
              </div>
              <div className="space-y-1">
                {(result.concerns || []).slice(0, 3).map((c, i) => (
                  <p key={i} className="text-sm text-[#111]">{c}</p>
                ))}
                {(!result.concerns || result.concerns.length === 0) && (
                  <p className="text-sm text-gray-400">No worries here!</p>
                )}
              </div>
            </div>
          </div>

          {/* Warm closing */}
          <div className="nb-card-sm bg-[#f5d0fe]/15 text-center animate-slide-up" style={{ animationDelay: "400ms" }} data-testid="encouragement">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Heart className="w-4 h-4 text-[#a21caf]" strokeWidth={2.5} />
              <span className="text-sm font-bold text-[#a21caf]">A quick reminder</span>
            </div>
            <p className="text-sm text-[#111] leading-relaxed">
              Everyone's at a different point in their AI journey — and that's perfectly fine.
              Honest answers help us build the right support for our team.
            </p>
          </div>

          <div className="text-center mt-6 animate-slide-up" style={{ animationDelay: "500ms" }}>
            <button onClick={() => navigate("/")} className="nb-btn-secondary" data-testid="back-home-btn">
              <Home className="w-4 h-4" strokeWidth={2.5} /> Back to start
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
